import type { CameraRig } from './camera-rig.js';

/**
 * F-31 Broadcast カメラモード — プリセットレジストリ。
 *
 * 公式大会の放送カメラ技法（BBC スヌーカー / Matchroom 9-ball / Mosconi Cup 等）を
 * シミュレーター上で再現するため、6 枠のプリセットスロットを **データ駆動レジストリ**
 * として 1 ファイルに集約する（`no-hardcoded-context` ルール準拠：将来追加が 1 行で済む）。
 *
 * **本ファイルは GPL 非依存**：`src/engine/**` / `src/render/vendor/**` を import しない。
 * 既存 rig API（GPL 非依存に保たれている）と数値型のみで完結する。
 *
 * 設計書: `knowledge/architecture/broadcast-camera-mode.md`
 * 機能ID: F-31（`system-design-traceability.md` §5）
 *
 * **P1 (MVP) スコープ**: プリセット #1 / #2 / #3 / #6 のみ実装。
 * #4 Jib Sweep（`panToKeyframes` 新規 API 要）と #5 Pocket Cam（動的算出）は P2 で追加。
 */

/** プリセット適用時に必要な盤面・ショット文脈（純データ・GPL 非依存）。
 *  Director 状態機械（P2）からも同型を入力に取る。 */
export interface SceneContext {
  /** 手玉位置（テーブル中心原点・z-up）。 */
  readonly cueBall: { x: number; y: number };
  /** 最若球（9 ボール）または狙う的玉。配置中・落球済み等で null 可。 */
  readonly targetBall: { x: number; y: number } | null;
  /** 自動照準が選んだポケット（プリセット #5 用・P1 では未使用）。 */
  readonly aimedPocket: { x: number; y: number } | null;
  /** 手玉→的玉のショット軸（ラジアン）。プリセット #1/#3 で azimuth 算出に使用。 */
  readonly shotAimAngle: number;
  /** テーブル外周半幅 X（長辺方向・テーブル中心からの距離）。 */
  readonly tableHalfX: number;
  /** テーブル外周半幅 Y（短辺方向）。 */
  readonly tableHalfY: number;
  /** ディレクター状態機械（P2）の入力。P1 では未使用。 */
  readonly phase: 'break' | 'normal' | 'win';
  /** ラック側（プリセット #3 の方向決定に必要）。 */
  readonly racker: 'head' | 'foot';
}

/** Broadcast プリセット ID 集合（P1 4 種＋ P2 で 2 種追加で計 6 種）。
 *  ハードコード防止のため、ID は `BROADCAST_PRESETS` 配列で 1 箇所管理する。
 *  P3（2026-06-26 (l) §14.1）で `hot-head` → `broadcast-master` へ rename
 *  （真俯瞰 88° を撤回し、BBC Masters 標準の短クッション側 20–25° へ意味変更）。 */
export type BroadcastPresetId =
  | 'player-pov'
  | 'broadcast-master'
  | 'low-rail'
  | 'jib-sweep'
  | 'pocket-cam'
  | 'beauty-wide';

/** プリセット定義（レジストリ要素）。`apply` は rig API のみを呼ぶ純関数。 */
export interface BroadcastPreset {
  readonly id: BroadcastPresetId;
  /** UI 表示名（i18n は後段で対応）。 */
  readonly displayName: string;
  /** HUD ピルバーの短ラベル（44px ボタンに収める用・3〜4 文字）。 */
  readonly shortLabel: string;
  /** キーボードショートカット（PC キーボード or Bluetooth キーボード接続時）。
   *  スマホ単体時は HUD ピルバー（上中央）のタップで切替する。 */
  readonly key: '1' | '2' | '3' | '4' | '5' | '6';
  /** 説明（設定パネル・キーバインド表示用）。 */
  readonly description: string;
  /** プリセット適用関数（rig へ既存 API で構図を反映）。 */
  readonly apply: (rig: CameraRig, ctx: SceneContext) => void;
}

/** プリセット #1 Player POV — エンドレール（撞いている側）からの構え視点。
 *  P1: 既存 `alignToAim` + 初期仰角相当 + 半径 2.4m。 */
const playerPov: BroadcastPreset = {
  id: 'player-pov',
  displayName: 'Player POV',
  shortLabel: 'POV',
  key: '1',
  description: 'エンドレール後方・ショット軸に沿う構え視点',
  apply: (rig, ctx) => {
    rig.clearOrbitTarget();
    rig.setAutoTopView(false);
    rig.setTopViewLocked(false);
    rig.setOverview(false);
    rig.setFollow(false);
    rig.setElevation(0.45);
    rig.setRadius(2.4);
    rig.alignToAim(ctx.shotAimAngle);
  },
};

/** プリセット #2 Broadcast Master (BM) — 短クッション側 20–25° の主役カメラ。
 *  P3 §14.3.2 で旧 Hot Head（真俯瞰 88°）から差替。BBC Masters Snooker の
 *  メインカメラ標準（20–22°・§13 F-1）に準拠し、テーブル全体が自然な遠近感で
 *  俯瞰される構図にする。topBlend を使わない（旧 Hot Head は使っていた）ため、
 *  topFitAz の旋回バグ（§13 F-6）は自動的に回避される。 */
const broadcastMaster: BroadcastPreset = {
  id: 'broadcast-master',
  displayName: 'Broadcast Master',
  shortLabel: 'BM',
  key: '2',
  description: '短クッション側 20–25° の主役カメラ（BBC Camera 1 構図）',
  apply: (rig, ctx) => {
    rig.setTopViewLocked(false);
    rig.setAutoTopView(false);
    rig.setOverview(false);
    rig.setFollow(false);
    rig.clearOrbitTarget();
    // 短クッション側（racker 側）からテーブル中心を見下ろす方位角。
    // racker='foot' なら +X 側にカメラを置く（azimuth=0 で +X 軸方向）。
    // alignToAim() は内部で desiredAzimuth = angle + π を設定するため、
    // 「camera を +X 側に置く」には angle=π を渡す（azimuth=0 になる）。
    const cushionAngle = ctx.racker === 'foot' ? Math.PI : 0;
    rig.alignToAim(cushionAngle);
    rig.setElevation(0.38); // ≈ 21.8°（BBC Masters 標準域）
    // テーブル長辺方向に沿う構図のため、半径は長辺 ≈2.54m の 1.1 倍 ≈2.8m。
    // 縦長ビュー（portrait）でも全球を画面内に収められる距離。
    rig.setRadius(2.8);
  },
};

/** プリセット #3 Low-Rail Cinematic — サイドクッション真横・ロー仰角。
 *  P2b: §12.5 補正で elevation 0.05→0.10（極端な迫り上がりを緩和して球を画面内に保持）。
 *  lookAt はショット軌道の中点（手玉・的玉の重心）へ寄せ、両球が常にフレーム中央に収まる
 *  ようにする。targetBall が null（落球直後）なら手玉単独。 */
const lowRail: BroadcastPreset = {
  id: 'low-rail',
  displayName: 'Low-Rail Cinematic',
  shortLabel: 'LOW',
  key: '3',
  description: 'サイドクッション真横・ロー仰角のシネマ構図',
  apply: (rig, ctx) => {
    rig.setTopViewLocked(false);
    rig.setAutoTopView(false);
    rig.setOverview(false);
    rig.setFollow(false);
    // ショット軸の真横（⊥）方向。手玉から左右どちらに置くかは racker 側に従う
    // （ラック側=foot のとき +Y、head のとき -Y 側の azimuth を選ぶ）。
    const perpendicular =
      ctx.shotAimAngle + (ctx.racker === 'foot' ? Math.PI / 2 : -Math.PI / 2);
    rig.alignToAim(perpendicular - Math.PI); // alignToAim 内部の +π を打ち消す
    rig.setElevation(0.10); // P2b: 0.05→0.10（球がフレーム外へ抜けにくくする）
    rig.setRadius(1.8);
    // P2b: lookAt をショット軌道の中点へ。手玉・的玉が常にフレーム中央に収まる構図に。
    const midX = (ctx.cueBall.x + (ctx.targetBall?.x ?? ctx.cueBall.x)) / 2;
    const midY = (ctx.cueBall.y + (ctx.targetBall?.y ?? ctx.cueBall.y)) / 2;
    rig.setOrbitTarget(midX, midY, 0);
  },
};

/** プリセット #4 Jib Sweep — 3 点ベジエ補間でフット上空 → ヘッド低空へトラベラー。
 *  P2b: §12.5 補正で 3.0→2.0 秒に短縮（長すぎ感の解消）。中継点 lookAt を手玉と的玉の
 *  重心へ向け、補間中も両球が常にフレーム中央付近に来るようにする。
 *  キーフレーム生成は SceneContext のテーブル半幅から動的算出（盤面サイズに追従）。 */
const jibSweep: BroadcastPreset = {
  id: 'jib-sweep',
  displayName: 'Jib Sweep',
  shortLabel: 'JIB',
  key: '4',
  description: 'フットレール上空 → ヘッドレール低空への 2 秒トラベラー',
  apply: (rig, ctx) => {
    rig.setTopViewLocked(false);
    rig.setAutoTopView(false);
    rig.setOverview(false);
    rig.setFollow(false);
    rig.clearOrbitTarget();
    // 3 点ベジエ: 始点（フットレール側上空・俯角強）→ 中継点（テーブル中央上空）
    // → 終点（ヘッドレール側ローレール）。テーブル中心原点・z-up・x が長辺。
    // racker='foot' のとき手玉はヘッド側 → フット上空からヘッドへ降下するスイープが自然。
    const startSign = ctx.racker === 'foot' ? +1 : -1;
    const hx = ctx.tableHalfX;
    const hy = ctx.tableHalfY;
    // P2b: 中継点・終点の lookAt を「手玉と的玉の重心」へ寄せ、補間中の球の見切れを抑える。
    const midX = (ctx.cueBall.x + (ctx.targetBall?.x ?? ctx.cueBall.x)) / 2;
    const midY = (ctx.cueBall.y + (ctx.targetBall?.y ?? ctx.cueBall.y)) / 2;
    const keyframes = [
      {
        // 始点: ラック側の角上 ≈ 1.8m 上空（テーブル中心を見て全景把握）
        pos: { x: startSign * (hx + 0.3), y: hy + 0.2, z: 1.8 },
        lookAt: { x: 0, y: 0, z: 0 },
      },
      {
        // 中継点: テーブル中央上空 ≈ 1.2m（重心 lookAt で球を中央に保持）
        pos: { x: 0, y: hy * 0.6, z: 1.2 },
        lookAt: { x: midX, y: midY, z: 0 },
      },
      {
        // 終点: 反対側レール近くのロー（z=0.18 ≈ クロス高さ）。lookAt は重心。
        pos: { x: -startSign * (hx + 0.15), y: hy * 0.3, z: 0.18 },
        lookAt: { x: midX, y: midY, z: 0 },
      },
    ];
    rig.panToKeyframes(keyframes, 2.0); // P2b: 3.0→2.0 秒
  },
};

/** プリセット #5 Pocket Cam — 狙ったポケットの斜め反対側からクローズアップ。
 *  P2: aimedPocket（ctx 由来）を優先・null の時は targetBall→cueBall 延長線上で
 *  最近接ポケットをフォールバック（呼び出し側 director で `fallbackTargetPocket` を使い ctx へ）。
 *  ポケット側のレール後方・height ≈ 0.5m・azimuth = ポケット→的玉。
 *  P2b: §12.5 補正で radius を「fixed 1.6」から `clamp(dist(target,pocket)*1.5 + 0.6, 1.3, 2.4)`
 *  の動的算出へ。的玉とポケットの距離が遠い構図でも両方をフレームに収める。 */
const pocketCam: BroadcastPreset = {
  id: 'pocket-cam',
  displayName: 'Pocket Cam',
  shortLabel: 'POCK',
  key: '5',
  description: '狙うポケットの斜め反対側からクローズアップ',
  apply: (rig, ctx) => {
    rig.setTopViewLocked(false);
    rig.setAutoTopView(false);
    rig.setOverview(false);
    rig.setFollow(false);
    const pocket = ctx.aimedPocket;
    const target = ctx.targetBall ?? ctx.cueBall;
    // フォールバック条件: ポケット未確定 or pocket と target が縮退（距離ゼロ）。
    // この場合は #1 Player POV へ落とす（設計書 §9 O-4 の 2 段フォールバック）。
    if (!pocket || Math.hypot(pocket.x - target.x, pocket.y - target.y) < 0.05) {
      rig.setElevation(0.45);
      rig.setRadius(2.4);
      rig.alignToAim(ctx.shotAimAngle);
      rig.clearOrbitTarget();
      return;
    }
    // azimuth = ポケット → 的玉 の方向（カメラはポケット側に立ち、的玉が画面奥）。
    const az = Math.atan2(target.y - pocket.y, target.x - pocket.x);
    rig.setOrbitTarget(target.x, target.y, 0);
    rig.alignToAim(az - Math.PI); // alignToAim 内部 +π を打ち消す
    rig.setElevation(0.35);
    // P2b: 動的 radius。dist(target,pocket) が遠いほど引いて両方を画面内に保つ。
    // 0.6m のベースに 1.5× 距離を加算、min 1.3m（過寄り防止）/ max 2.4m（過引き防止）でクランプ。
    const distTP = Math.hypot(target.x - pocket.x, target.y - pocket.y);
    const dynamicRadius = Math.max(1.3, Math.min(2.4, 0.6 + distTP * 1.5));
    rig.setRadius(dynamicRadius);
  },
};

/** プリセット #6 Beauty Wide — コーナーから 35° 上空のテーブル全景。
 *  P1: 既存 `setOverview(true)` + azimuth = 45°。 */
const beautyWide: BroadcastPreset = {
  id: 'beauty-wide',
  displayName: 'Beauty Wide',
  shortLabel: 'WIDE',
  key: '6',
  description: 'コーナーから 35° 上空のテーブル全景',
  apply: (rig, _ctx) => {
    rig.setTopViewLocked(false);
    rig.setAutoTopView(false);
    rig.setFollow(false);
    rig.clearOrbitTarget();
    rig.alignToAim(Math.PI / 4 - Math.PI); // azimuth = π/4（コーナー方向）
    rig.setElevation(0.92);
    rig.setOverview(true);
  },
};

/** Broadcast プリセットの全レジストリ。
 *  P1 で #1/#2/#3/#6 の 4 件、P2 で #4 Jib Sweep / #5 Pocket Cam を追加して計 6 件。
 *  追加時は本配列に 1 行追加するだけで HUD バー・キーバインド・設定 UI が自動追従する設計
 *  （ハードコード禁止ルール準拠）。 */
export const BROADCAST_PRESETS: readonly BroadcastPreset[] = [
  playerPov,
  broadcastMaster,
  lowRail,
  jibSweep,
  pocketCam,
  beautyWide,
];

/** id 検索ヘルパ（main / settings から参照）。 */
export const getBroadcastPreset = (
  id: BroadcastPresetId,
): BroadcastPreset | null => BROADCAST_PRESETS.find((p) => p.id === id) ?? null;

/** key 検索ヘルパ（main の keydown ハンドラ用）。 */
export const findBroadcastPresetByKey = (
  key: string,
): BroadcastPreset | null =>
  BROADCAST_PRESETS.find((p) => p.key === key) ?? null;
